import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import {
  getActivityTypeColor,
  getActivityTypeLabel,
} from '@shared/helpers/design/activities.helpers';

type ActivityColorDotProps = {
  type: ActivityType | Activity;
} & React.HTMLAttributes<HTMLSpanElement>;

type ColorDotParams = {
  color: string;
};

/**
 * Renders a colored dot indicator for an activity type
 * @param type ActivityType or Activity to get color from
 */
export const ActivityColorDot: React.FC<ActivityColorDotProps> = ({
  type,
  className,
  ...props
}) => {
  const color = getActivityTypeColor(type);
  const { classes, cx } = useColorDotStyles({ color });

  // Get label: use activity name if available, otherwise use type label
  const label =
    typeof type === 'object' && 'name' in type && type.name
      ? type.name
      : getActivityTypeLabel(type);

  return (
    <span
      className={cx(classes.root, className)}
      aria-label={`Activity color ${label}`}
      {...props}
    />
  );
};

const useColorDotStyles = makeStyles<ColorDotParams>()((theme, { color }) => ({
  root: {
    display: 'inline-flex',
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: '2px',
    border: `1px solid ${theme.palette.common.black}`,
    backgroundColor: color,
    flexShrink: 0,
  },
}));
